/**
 * Thai Typography Utilities
 * Provides utilities for proper Thai text rendering and formatting
 */

/**
 * Thai font stack with proper fallbacks
 */
export const THAI_FONT_STACK = [
  'Sarabun',
  'Noto Sans Thai',
  'Kanit',
  'Prompt',
  'Mitr',
  'Chakra Petch',
  'Bai Jamjuree',
  'Sukhumvit Set',
  'Thonburi',
  'Ayuthaya',
  'Krungthep',
  'sans-serif'
].join(', ');

/**
 * Thai text line height configurations
 */
export const THAI_LINE_HEIGHTS = {
  tight: 1.4,
  normal: 1.6,
  relaxed: 1.8,
  loose: 2.0
};

/**
 * Thai text font sizes (optimized for Thai characters)
 */
export const THAI_FONT_SIZES = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem'  // 36px
};

/**
 * Check if text contains Thai characters
 * @param {string} text - Text to check
 * @returns {boolean} - True if text contains Thai characters
 */
export function containsThai(text) {
  if (!text || typeof text !== 'string') return false;
  
  // Thai Unicode range: U+0E00–U+0E7F
  const thaiRegex = /[\u0E00-\u0E7F]/;
  return thaiRegex.test(text);
}

/**
 * Apply proper word breaking for Thai text
 * @param {string} text - Thai text to process
 * @returns {string} - Text with proper word breaking
 */
export function applyThaiWordBreaking(text) {
  if (!containsThai(text)) return text;
  
  // Add zero-width space after common Thai punctuation for better line breaking
  return text
    .replace(/([ๆฯ])/g, '$1\u200B') // After repetition marks
    .replace(/([๐-๙])/g, '$1\u200B') // After Thai numerals
    .replace(/([ก-ฮ][ะ-์])/g, '$1\u200B') // After complete syllables
    .replace(/([।॥])/g, '$1\u200B'); // After Thai punctuation
}

/**
 * Get optimal line height for Thai text based on font size
 * @param {number} fontSize - Font size in pixels
 * @returns {number} - Optimal line height
 */
export function getThaiLineHeight(fontSize) {
  if (fontSize <= 14) return THAI_LINE_HEIGHTS.relaxed;
  if (fontSize <= 18) return THAI_LINE_HEIGHTS.normal;
  if (fontSize <= 24) return THAI_LINE_HEIGHTS.normal;
  return THAI_LINE_HEIGHTS.tight;
}

/**
 * Create CSS class for Thai typography
 * @param {Object} options - Typography options
 * @returns {Object} - CSS styles object
 */
export function createThaiTypographyStyles(options = {}) {
  const {
    fontSize = THAI_FONT_SIZES.base,
    lineHeight = THAI_LINE_HEIGHTS.normal,
    fontWeight = '400',
    letterSpacing = 'normal'
  } = options;

  return {
    fontFamily: THAI_FONT_STACK,
    fontSize,
    lineHeight,
    fontWeight,
    letterSpacing,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    hyphens: 'auto',
    // Improve text rendering for Thai characters
    textRendering: 'optimizeLegibility',
    fontFeatureSettings: '"liga" 1, "kern" 1',
    fontVariantLigatures: 'common-ligatures',
    // Better font smoothing for Thai fonts
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale'
  };
}

/**
 * Vue composable for Thai typography
 */
export function useThaiTypography() {
  /**
   * Apply Thai typography to an element
   * @param {HTMLElement} element - Target element
   * @param {Object} options - Typography options
   */
  const applyThaiTypography = (element, options = {}) => {
    if (!element) return;
    
    const styles = createThaiTypographyStyles(options);
    Object.assign(element.style, styles);
  };

  /**
   * Check if element contains Thai text and apply appropriate styles
   * @param {HTMLElement} element - Target element
   */
  const autoApplyThaiTypography = (element) => {
    if (!element) return;
    
    const text = element.textContent || element.innerText || '';
    if (containsThai(text)) {
      applyThaiTypography(element);
      
      // Apply word breaking
      element.innerHTML = applyThaiWordBreaking(element.innerHTML);
    }
  };

  return {
    containsThai,
    applyThaiWordBreaking,
    getThaiLineHeight,
    createThaiTypographyStyles,
    applyThaiTypography,
    autoApplyThaiTypography,
    THAI_FONT_STACK,
    THAI_LINE_HEIGHTS,
    THAI_FONT_SIZES
  };
}

/**
 * Vue directive for automatic Thai typography
 */
export const vThaiTypography = {
  mounted(el, binding) {
    const { autoApplyThaiTypography } = useThaiTypography();
    autoApplyThaiTypography(el);
  },
  updated(el, binding) {
    const { autoApplyThaiTypography } = useThaiTypography();
    autoApplyThaiTypography(el);
  }
};

export default {
  containsThai,
  applyThaiWordBreaking,
  getThaiLineHeight,
  createThaiTypographyStyles,
  useThaiTypography,
  vThaiTypography,
  THAI_FONT_STACK,
  THAI_LINE_HEIGHTS,
  THAI_FONT_SIZES
};